// ✅ REDESIGNED LOGIN: Role-First Design with all UX improvements
// Improvements implemented: #1-9 from recommendations
import { useState } from "react";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useNavigate, Link } from "react-router-dom";
import { ROLES, STATUSES } from "../constants/roles";
import { db, auth } from "../firebase";

export default function LoginRoleFirst() {
  const navigate = useNavigate();

  // Role selection state
  const [selectedRole, setSelectedRole] = useState(null); // null | 'teacher' | 'student'

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");

  // Password reset state
  const [showResetInput, setShowResetInput] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");

  // Email validation hint
  const [emailHint, setEmailHint] = useState("");

  const redirectBasedOnRole = async (uid) => {
    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const userData = userSnap.data();
      const status = userData.status;

      if (status === STATUSES.PENDING) {
        navigate("/pending-approval");
        return;
      }

      if (status === STATUSES.DISABLED) {
        setError("❌ Account Suspended\n\nYour account has been disabled. Please contact support.");
        setLoading(false);
        return;
      }

      navigate("/my-dashboard");
    } else {
      navigate("/pending-approval");
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setLoadingMessage("Signing in...");

    try {
      // Set persistence based on "Remember Me" checkbox
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);

      // Convert username to email format for students
      let loginEmail = email;
      if (selectedRole === 'student' && !email.includes('@')) {
        loginEmail = `${email.toLowerCase().trim()}@examlock.student`;
      }

      console.log('🔐 Login attempt:', {
        role: selectedRole,
        input: email,
        convertedEmail: loginEmail,
        passwordLength: password.length
      });

      const result = await signInWithEmailAndPassword(auth, loginEmail, password);
      await redirectBasedOnRole(result.user.uid);
    } catch (err) {
      setLoading(false);

      // Improved error messages (#9)
      if (err.code === 'auth/wrong-password') {
        setError("❌ Incorrect password\n\nTry again or click 'Forgot Password?' below");
      } else if (err.code === 'auth/user-not-found') {
        setError(`❌ Email not found\n\nAre you a ${selectedRole === 'teacher' ? 'student' : 'teacher'}? Try switching roles.\nNew ${selectedRole}? Register first.`);
      } else if (err.code === 'auth/invalid-email') {
        setError("❌ Invalid email format\n\nPlease enter a valid email address");
      } else {
        setError(`❌ Sign in failed\n\n${err.message}`);
      }
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);
    setLoadingMessage("🔍 Redirecting to Google...");

    const provider = new GoogleAuthProvider();
    provider.addScope('https://www.googleapis.com/auth/classroom.courses.readonly');
    provider.addScope('https://www.googleapis.com/auth/classroom.rosters.readonly');
    provider.addScope('https://www.googleapis.com/auth/classroom.profile.emails');
    provider.setCustomParameters({
      prompt: 'consent',
      access_type: 'offline'
    });

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      setLoadingMessage("🔍 Importing your classes...");

      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential?.accessToken;

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        await signOut(auth);
        setLoading(false);
        setError("❌ No account found\n\nPlease register first before signing in with Google.");
        return;
      }

      await setDoc(userRef, {
        oauthProviders: userSnap.data().oauthProviders ?
          [...new Set([...userSnap.data().oauthProviders, 'google'])] : ['google'],
        hasClassroomAccess: !!accessToken,
      }, { merge: true });

      await redirectBasedOnRole(user.uid);
    } catch (err) {
      setLoading(false);
      setError(`❌ Google sign in failed\n\n${err.message}`);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError("");
    setLoading(true);
    setLoadingMessage("📋 Redirecting to Microsoft...");

    const provider = new OAuthProvider('microsoft.com');
    provider.addScope('https://graph.microsoft.com/User.Read');
    provider.addScope('https://graph.microsoft.com/Group.Read.All');
    provider.addScope('https://graph.microsoft.com/GroupMember.Read.All');
    provider.addScope('https://graph.microsoft.com/EduRoster.ReadBasic');
    provider.setCustomParameters({
      prompt: 'consent',
      response_type: 'code'
    });

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      setLoadingMessage("📋 Importing your classes...");

      const credential = OAuthProvider.credentialFromResult(result);
      const accessToken = credential?.accessToken;

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        await signOut(auth);
        setLoading(false);
        setError("❌ No account found\n\nPlease register first before signing in with Microsoft.");
        return;
      }

      await setDoc(userRef, {
        oauthProviders: userSnap.data().oauthProviders ?
          [...new Set([...userSnap.data().oauthProviders, 'microsoft'])] : ['microsoft'],
        hasTeamsAccess: !!accessToken,
      }, { merge: true });

      await redirectBasedOnRole(user.uid);
    } catch (err) {
      setLoading(false);
      setError(`❌ Microsoft sign in failed\n\n${err.message}`);
    }
  };

  const handleResetPassword = async () => {
    if (!resetEmail) {
      setResetMessage("Please enter your email address");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage("✅ Password reset email sent! Check your inbox.");
    } catch (err) {
      setResetMessage(`❌ ${err.message}`);
    }
  };

  // Smart email validation (#6)
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (value.length > 0) {
      // Students can use username (no @ required)
      if (selectedRole === 'teacher' && !value.includes('@')) {
        setEmailHint("⚠️ Email must include @");
      } else if (selectedRole === 'teacher' && !value.includes('.')) {
        setEmailHint("💡 Use your school email address");
      } else if (selectedRole === 'student' && !value.includes('@')) {
        setEmailHint("✓ Using username format");
      } else {
        setEmailHint("✓ Valid email format");
      }
    } else {
      setEmailHint("");
    }
  };

  // ROLE SELECTION SCREEN (Improvement #1)
  if (!selectedRole) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-indigo-50 via-blue-50 to-sky-100 px-4 py-8">
        <div className="max-w-4xl w-full">

          {/* Trust Signals */}
          <div className="text-center mb-8">
            <p className="text-sm text-gray-600">
              Private beta · Browser-based secure testing · Built for GCC schools
            </p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-12">
            {/* Header */}
            <div className="text-center mb-10">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-3">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h1 className="text-4xl font-bold text-gray-900">ExamLock</h1>
              </div>
              <p className="text-xl text-gray-600 font-medium">Secure Exam Platform</p>
              <p className="text-gray-500 mt-2">Choose your role to continue</p>
            </div>

            {/* Role Cards */}
            <div className="mb-8">
              <p className="text-center text-lg font-semibold text-gray-700 mb-6">I am a...</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Teacher Card */}
                <button
                  onClick={() => setSelectedRole('teacher')}
                  className="group relative bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border-2 border-blue-200 hover:border-blue-400 rounded-xl p-8 transition-all duration-200 hover:shadow-xl"
                >
                  <div className="text-center">
                    <div className="text-6xl mb-4">👩‍🏫</div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">TEACHER</h2>
                    <p className="text-gray-600 text-sm mb-4">Create & monitor exams</p>
                    <div className="flex items-center justify-center text-blue-600 font-semibold">
                      <span>Sign In</span>
                      <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </button>

                {/* Student Card */}
                <button
                  onClick={() => setSelectedRole('student')}
                  className="group relative bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 border-2 border-purple-200 hover:border-purple-400 rounded-xl p-8 transition-all duration-200 hover:shadow-xl"
                >
                  <div className="text-center">
                    <div className="text-6xl mb-4">👨‍🎓</div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">STUDENT</h2>
                    <p className="text-gray-600 text-sm mb-4">Take exams securely</p>
                    <div className="flex items-center justify-center text-purple-600 font-semibold">
                      <span>Sign In</span>
                      <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Bottom Links */}
            <div className="text-center space-y-3 pt-6 border-t border-gray-200">
              <p className="text-gray-600 text-sm">New to ExamLock?</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/teacher-register"
                  className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Start Free Trial
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center px-6 py-3 border-2 border-gray-300 hover:border-gray-400 text-gray-700 hover:text-gray-900 rounded-lg font-medium transition-all duration-200"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>

          {/* Back to Home Link */}
          <div className="text-center mt-6">
            <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // SIGN-IN FORM (After role selected)
  const isTeacher = selectedRole === 'teacher';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-indigo-50 via-blue-50 to-sky-100 px-4 py-8">
      <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md">

        {/* Role Indicator with Back Button (#2 - Easy role switching) */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => {
              setSelectedRole(null);
              setError("");
              setEmail("");
              setPassword("");
            }}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm font-medium">Change Role</span>
          </button>

          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${isTeacher ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
            <span className="text-lg">{isTeacher ? '👩‍🏫' : '👨‍🎓'}</span>
            <span className="font-semibold text-sm">{isTeacher ? 'Teacher' : 'Student'}</span>
          </div>
        </div>

        {/* Header with role-specific messaging (#4) */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            {isTeacher ? 'Welcome back, Teacher! 👋' : 'Hi Student! 📚'}
          </h1>
          <p className="text-gray-600">
            {isTeacher
              ? 'Sign in with your school account'
              : 'Enter the login details your teacher gave you'}
          </p>
        </div>

        {/* Loading State (#8) */}
        {loading && (
          <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg flex items-center">
            <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span>{loadingMessage || 'Loading...'}</span>
          </div>
        )}

        {/* Error Messages (#9 - Improved) */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-300 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <pre className="text-sm text-red-700 whitespace-pre-wrap font-sans">{error}</pre>
            </div>
          </div>
        )}

        {/* OAuth Buttons (Primary for teachers) - Temporarily hidden until verification complete */}
        {/* TODO: Re-enable after completing Google/Microsoft OAuth verification */}
        {false && isTeacher && (
          <div className="mb-6 space-y-3">
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-blue-400 rounded-lg p-4 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0 text-2xl mr-4">🔍</div>
                <div className="flex-1 text-left">
                  <div className="font-semibold text-gray-900 group-hover:text-blue-600">
                    Continue with Google Classroom
                  </div>
                  <div className="text-sm text-gray-500">
                    ✓ Auto-imports classes & students
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>

            <button
              onClick={handleMicrosoftLogin}
              disabled={loading}
              className="w-full bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-blue-400 rounded-lg p-4 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0 text-2xl mr-4">📋</div>
                <div className="flex-1 text-left">
                  <div className="font-semibold text-gray-900 group-hover:text-blue-600">
                    Continue with Microsoft Teams
                  </div>
                  <div className="text-sm text-gray-500">
                    ✓ Sync classes instantly
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">or use email</span>
              </div>
            </div>
          </div>
        )}

        {/* Email/Password Form */}
        <form onSubmit={handleEmailLogin} className="space-y-4">
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              {isTeacher ? 'Email Address' : 'Student ID or Email'}
            </label>
            <input
              type={isTeacher ? "email" : "text"}
              placeholder={isTeacher ? "teacher@school.edu" : "aya.abdillahi"}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              value={email}
              onChange={handleEmailChange}
              disabled={loading}
              required
              autoComplete={isTeacher ? "email" : "username"}
              autoCapitalize="none"
              spellCheck="false"
            />
            {/* Smart validation hint (#6) */}
            {emailHint && (
              <p className={`text-xs mt-1 ${emailHint.includes('✓') ? 'text-green-600' : emailHint.includes('⚠️') ? 'text-orange-600' : 'text-blue-600'}`}>
                {emailHint}
              </p>
            )}
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              At least 8 characters
            </p>
          </div>

          {/* Remember Me Checkbox */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700 cursor-pointer select-none">
                Remember me
              </label>
            </div>

            {isTeacher && (
              <button
                type="button"
                onClick={() => setShowResetInput(!showResetInput)}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
              >
                Forgot Password?
              </button>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full ${isTeacher ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700' : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'} text-white py-3 px-4 rounded-lg font-semibold transition duration-200 text-base disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg`}
          >
            {loading ? 'Signing in...' : isTeacher ? 'Sign In' : 'Start Exam'}
          </button>

          {/* Password Reset (#7 - Better UX) */}
          {showResetInput && (
            <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-700 font-medium">Reset your password</p>
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
              <button
                type="button"
                onClick={handleResetPassword}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-lg font-medium transition duration-200"
              >
                Send Reset Email
              </button>
              {resetMessage && (
                <p className={`text-sm text-center ${resetMessage.includes('✅') ? 'text-green-600' : 'text-red-600'}`}>
                  {resetMessage}
                </p>
              )}
            </div>
          )}
        </form>

        {/* Student-specific help text (#4) */}
        {!isTeacher && (
          <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-sm text-purple-900 font-medium mb-1">❓ Need help?</p>
            <p className="text-sm text-purple-700">
              Ask your teacher for your account information
            </p>
          </div>
        )}

        {/* Teacher-specific registration CTA */}
        {isTeacher && (
          <div className="mt-6 text-center space-y-3">
            <p className="text-sm text-gray-500">
              New to ExamLock?
            </p>
            <Link
              to="/teacher-register"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-semibold"
            >
              Start Free Trial
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
